import { Float, Text } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { CuboidCollider, RigidBody } from '@react-three/rapier'
import { useRef, useState, useMemo } from 'react'
import * as THREE from 'three'
import useGame from './stores/useGame'

// Shared geometry
const boxGeometry = new THREE.BoxGeometry(1, 1, 1)
const coneGeometry = new THREE.ConeGeometry(1, 1, 8)

// Materials
const floor1Material = new THREE.MeshStandardMaterial({
    color: '#e0f2fe',
    metalness: 0.1,
    roughness: 0.3
})

const floor2Material = new THREE.MeshStandardMaterial({
    color: '#bae6fd',
    metalness: 0.2,
    roughness: 0.25
})

const obstacleMaterial = new THREE.MeshStandardMaterial({
    color: '#7dd3fc',
    metalness: 0.5,
    roughness: 0.2,
    transparent: true,
    opacity: 0.9
})

const wallMaterial = new THREE.MeshStandardMaterial({
    color: '#a5f3fc',
    metalness: 0.8,
    roughness: 0.1,
    transparent: true,
    opacity: 0.7
})

// Tree materials - full opacity decorations
const treeTrunkMaterial = new THREE.MeshStandardMaterial({
    color: '#5d4037'
})
const treeLeavesMaterial = new THREE.MeshStandardMaterial({
    color: '#1b5e20',
    metalness: 0.1,
    roughness: 0.8
})
const snowMaterial = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    metalness: 0.1,
    roughness: 0.9
})

// Pine Tree component
function PineTree({ position, scale = 1 }) {
    return (
        <group position={position} scale={scale}>
            {/* Trunk */}
            <mesh position={[0, 0.3, 0]} castShadow material={treeTrunkMaterial}>
                <cylinderGeometry args={[0.1, 0.15, 0.6, 8]} />
            </mesh>
            {/* Tree layers */}
            <mesh position={[0, 0.8, 0]} castShadow material={treeLeavesMaterial}>
                <coneGeometry args={[0.5, 0.8, 8]} />
            </mesh>
            <mesh position={[0, 1.3, 0]} castShadow material={treeLeavesMaterial}>
                <coneGeometry args={[0.4, 0.7, 8]} />
            </mesh>
            <mesh position={[0, 1.7, 0]} castShadow material={treeLeavesMaterial}>
                <coneGeometry args={[0.25, 0.5, 8]} />
            </mesh>
            {/* Snow on top */}
            <mesh position={[0, 1.95, 0]} material={snowMaterial}>
                <coneGeometry args={[0.15, 0.2, 8]} />
            </mesh>
        </group>
    )
}

// Snowman component
function Snowman({ position }) {
    return (
        <group position={position}>
            {/* Body */}
            <mesh position={[0, 0.25, 0]} castShadow material={snowMaterial}>
                <sphereGeometry args={[0.25, 16, 16]} />
            </mesh>
            <mesh position={[0, 0.55, 0]} castShadow material={snowMaterial}>
                <sphereGeometry args={[0.18, 16, 16]} />
            </mesh>
            <mesh position={[0, 0.78, 0]} castShadow material={snowMaterial}>
                <sphereGeometry args={[0.12, 16, 16]} />
            </mesh>
            {/* Eyes */}
            <mesh position={[-0.04, 0.82, 0.1]}>
                <sphereGeometry args={[0.02, 8, 8]} />
                <meshStandardMaterial color="#1a1a1a" />
            </mesh>
            <mesh position={[0.04, 0.82, 0.1]}>
                <sphereGeometry args={[0.02, 8, 8]} />
                <meshStandardMaterial color="#1a1a1a" />
            </mesh>
            {/* Nose */}
            <mesh position={[0, 0.78, 0.12]} rotation={[Math.PI / 2, 0, 0]}>
                <coneGeometry args={[0.02, 0.1, 8]} />
                <meshStandardMaterial color="#ff6600" />
            </mesh>
        </group>
    )
}

// Ice Rock component - full opacity
function IceRock({ position, scale = 1 }) {
    return (
        <mesh position={position} scale={scale} castShadow>
            <dodecahedronGeometry args={[0.3, 0]} />
            <meshStandardMaterial
                color="#b3e5fc"
                metalness={0.3}
                roughness={0.4}
            />
        </mesh>
    )
}

// Crystal component (simplified - no extra collider box)
function Crystal({ position }) {
    const ref = useRef()
    const glowRef = useRef()
    const [collected, setCollected] = useState(false)
    const collectCrystal = useGame((state) => state.collectCrystal)

    useFrame((state) => {
        if (ref.current && !collected) {
            const time = state.clock.getElapsedTime()
            ref.current.rotation.y = time * 2
            ref.current.position.y = Math.sin(time * 3) * 0.15 + 0.6
        }
    })

    const handleCollision = () => {
        const phase = useGame.getState().phase
        if (!collected && phase === 'playing') {
            setCollected(true)
            collectCrystal()
        }
    }

    if (collected) return null

    return (
        <group position={position}>
            <RigidBody
                type="fixed"
                colliders="ball"
                onIntersectionEnter={handleCollision}
                sensor
            >
                <group ref={ref}>
                    <mesh castShadow>
                        <octahedronGeometry args={[0.3, 0]} />
                        <meshStandardMaterial
                            color="#22d3ee"
                            emissive="#06b6d4"
                            emissiveIntensity={1}
                            metalness={0.9}
                            roughness={0.1}
                            transparent
                            opacity={0.9}
                        />
                    </mesh>
                    <mesh>
                        <octahedronGeometry args={[0.15, 0]} />
                        <meshStandardMaterial
                            color="#ffffff"
                            emissive="#ffffff"
                            emissiveIntensity={2}
                        />
                    </mesh>
                    <mesh ref={glowRef}>
                        <sphereGeometry args={[0.5, 16, 16]} />
                        <meshBasicMaterial
                            color="#22d3ee"
                            transparent
                            opacity={0.15}
                        />
                    </mesh>
                </group>
            </RigidBody>
        </group>
    )
}

// BlockStart - Starting platform
export function BlockStart({ position = [0, 0, 0] })
{
    return (
        <group position={position}>
            <Float floatIntensity={0.25} rotationIntensity={0.25}>
                <Text
                    font="/bebas-neue-v9-latin-regular.woff"
                    scale={0.5}
                    maxWidth={0.25}
                    lineHeight={0.75}
                    textAlign="right"
                    position={[0.75, 0.65, 0]}
                    rotation-y={-0.25}
                >
                    GO!
                    <meshStandardMaterial
                        color="#ffffff"
                        emissive="#22d3ee"
                        emissiveIntensity={0.8}
                    />
                </Text>
            </Float>

            {/* Floor with RigidBody */}
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={floor1Material}
                />
            </RigidBody>
        </group>
    )
}

// Finish Gate material - Icy theme
const finishGateMaterial = new THREE.MeshStandardMaterial({
    color: '#67e8f9',
    emissive: '#22d3ee',
    emissiveIntensity: 0.4,
    metalness: 0.9,
    roughness: 0.1
})

const finishGlowMaterial = new THREE.MeshStandardMaterial({
    color: '#a5f3fc',
    emissive: '#22d3ee',
    emissiveIntensity: 1,
    metalness: 0.8,
    roughness: 0.1,
    transparent: true,
    opacity: 0.9
})

// BlockEnd - Finish platform with beautiful gate
export function BlockEnd({ position = [0, 0, 0] })
{
    const starRef = useRef()
    const ring1Ref = useRef()
    const ring2Ref = useRef()
    const particlesRef = useRef()

    useFrame((state) => {
        const time = state.clock.getElapsedTime()

        // Trophy rotation and float
        if (starRef.current) {
            starRef.current.rotation.y = time * 0.8
            starRef.current.position.y = Math.sin(time * 2) * 0.1 + 1.8
        }

        // Rotating rings
        if (ring1Ref.current) {
            ring1Ref.current.rotation.z = time * 0.5
            ring1Ref.current.rotation.x = Math.sin(time * 0.3) * 0.2
        }
        if (ring2Ref.current) {
            ring2Ref.current.rotation.z = -time * 0.7
            ring2Ref.current.rotation.y = Math.cos(time * 0.4) * 0.2
        }
    })

    // Finish floor material - muzlik rang
    const finishFloorMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#a5f3fc',
        metalness: 0.3,
        roughness: 0.2
    }), [])

    return (
        <group position={position}>
            {/* Floor - collider floor bilan bir xil pozitsiyada */}
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={finishFloorMaterial}
                />
            </RigidBody>

            {/* Finish Gate - Arch - Icy Theme */}
            <group position={[0, 0, 1.5]}>
                {/* Left pillar */}
                <mesh position={[-1.5, 1.2, 0]} castShadow>
                    <boxGeometry args={[0.3, 2.4, 0.3]} />
                    <meshStandardMaterial
                        color="#67e8f9"
                        emissive="#22d3ee"
                        emissiveIntensity={0.3}
                        metalness={0.9}
                        roughness={0.1}
                    />
                </mesh>
                {/* Left pillar base */}
                <mesh position={[-1.5, 0.15, 0]} castShadow>
                    <boxGeometry args={[0.5, 0.3, 0.5]} />
                    <meshStandardMaterial color="#a5f3fc" emissive="#22d3ee" emissiveIntensity={0.2} metalness={0.9} roughness={0.1} />
                </mesh>
                {/* Left pillar top */}
                <mesh position={[-1.5, 2.5, 0]} castShadow>
                    <boxGeometry args={[0.4, 0.2, 0.4]} />
                    <meshStandardMaterial color="#a5f3fc" emissive="#22d3ee" emissiveIntensity={0.2} metalness={0.9} roughness={0.1} />
                </mesh>

                {/* Right pillar */}
                <mesh position={[1.5, 1.2, 0]} castShadow>
                    <boxGeometry args={[0.3, 2.4, 0.3]} />
                    <meshStandardMaterial
                        color="#67e8f9"
                        emissive="#22d3ee"
                        emissiveIntensity={0.3}
                        metalness={0.9}
                        roughness={0.1}
                    />
                </mesh>
                {/* Right pillar base */}
                <mesh position={[1.5, 0.15, 0]} castShadow>
                    <boxGeometry args={[0.5, 0.3, 0.5]} />
                    <meshStandardMaterial color="#a5f3fc" emissive="#22d3ee" emissiveIntensity={0.2} metalness={0.9} roughness={0.1} />
                </mesh>
                {/* Right pillar top */}
                <mesh position={[1.5, 2.5, 0]} castShadow>
                    <boxGeometry args={[0.4, 0.2, 0.4]} />
                    <meshStandardMaterial color="#a5f3fc" emissive="#22d3ee" emissiveIntensity={0.2} metalness={0.9} roughness={0.1} />
                </mesh>

                {/* Top bar */}
                <mesh position={[0, 2.7, 0]} castShadow>
                    <boxGeometry args={[3.4, 0.25, 0.25]} />
                    <meshStandardMaterial
                        color="#67e8f9"
                        emissive="#22d3ee"
                        emissiveIntensity={0.4}
                        metalness={0.9}
                        roughness={0.1}
                    />
                </mesh>

                {/* FINISH text on arch */}
                <Text
                    font="/bebas-neue-v9-latin-regular.woff"
                    scale={0.6}
                    position={[0, 3.1, 0]}
                >
                    FINISH
                    <meshStandardMaterial
                        color="#ffffff"
                        emissive="#22d3ee"
                        emissiveIntensity={1.5}
                    />
                </Text>

                {/* Decorative crystals on pillars */}
                <mesh position={[-1.5, 2.1, 0.2]} rotation={[0, 0, Math.PI / 4]}>
                    <octahedronGeometry args={[0.15, 0]} />
                    <meshStandardMaterial color="#67e8f9" emissive="#22d3ee" emissiveIntensity={1} />
                </mesh>
                <mesh position={[1.5, 2.1, 0.2]} rotation={[0, 0, Math.PI / 4]}>
                    <octahedronGeometry args={[0.15, 0]} />
                    <meshStandardMaterial color="#67e8f9" emissive="#22d3ee" emissiveIntensity={1} />
                </mesh>
            </group>

            {/* Center Trophy Platform */}
            <group position={[0, 0, -0.5]}>
                {/* Pedestal */}
                <mesh position={[0, 0.2, 0]} castShadow>
                    <cylinderGeometry args={[0.6, 0.8, 0.4, 8]} />
                    <meshStandardMaterial
                        color="#e0f2fe"
                        metalness={0.3}
                        roughness={0.2}
                    />
                </mesh>
                <mesh position={[0, 0.5, 0]} castShadow>
                    <cylinderGeometry args={[0.5, 0.6, 0.2, 8]} />
                    <meshStandardMaterial
                        color="#bae6fd"
                        metalness={0.4}
                        roughness={0.2}
                    />
                </mesh>

                {/* Rotating rings - Icy */}
                <group ref={ring1Ref} position={[0, 1.5, 0]}>
                    <mesh>
                        <torusGeometry args={[0.7, 0.05, 16, 32]} />
                        <meshStandardMaterial
                            color="#a5f3fc"
                            emissive="#22d3ee"
                            emissiveIntensity={0.5}
                            metalness={0.9}
                            roughness={0.1}
                        />
                    </mesh>
                </group>
                <group ref={ring2Ref} position={[0, 1.5, 0]}>
                    <mesh rotation={[Math.PI / 2, 0, 0]}>
                        <torusGeometry args={[0.55, 0.04, 16, 32]} />
                        <meshStandardMaterial
                            color="#67e8f9"
                            emissive="#0891b2"
                            emissiveIntensity={0.6}
                            metalness={0.9}
                            roughness={0.1}
                        />
                    </mesh>
                </group>

                {/* Main Trophy Crystal - Icy */}
                <group ref={starRef} position={[0, 1.8, 0]}>
                    {/* Outer crystal */}
                    <mesh castShadow>
                        <octahedronGeometry args={[0.5, 0]} />
                        <meshStandardMaterial
                            color="#67e8f9"
                            emissive="#22d3ee"
                            emissiveIntensity={1}
                            metalness={0.8}
                            roughness={0.1}
                            transparent
                            opacity={0.9}
                        />
                    </mesh>
                    {/* Inner glow */}
                    <mesh>
                        <octahedronGeometry args={[0.3, 0]} />
                        <meshStandardMaterial
                            color="#ffffff"
                            emissive="#ffffff"
                            emissiveIntensity={2}
                        />
                    </mesh>
                    {/* Glow sphere */}
                    <mesh>
                        <sphereGeometry args={[0.7, 16, 16]} />
                        <meshBasicMaterial
                            color="#22d3ee"
                            transparent
                            opacity={0.15}
                        />
                    </mesh>
                </group>
            </group>

            {/* Ground decorations - small icy crystals */}
            <mesh position={[-1.2, 0.15, -0.8]} rotation={[0, 0.5, 0]}>
                <octahedronGeometry args={[0.12, 0]} />
                <meshStandardMaterial color="#67e8f9" emissive="#22d3ee" emissiveIntensity={0.8} transparent opacity={0.9} />
            </mesh>
            <mesh position={[1.3, 0.12, -0.6]} rotation={[0, -0.3, 0]}>
                <octahedronGeometry args={[0.1, 0]} />
                <meshStandardMaterial color="#a5f3fc" emissive="#0891b2" emissiveIntensity={0.8} transparent opacity={0.9} />
            </mesh>
            <mesh position={[0.8, 0.1, 0.5]} rotation={[0, 1.2, 0]}>
                <octahedronGeometry args={[0.08, 0]} />
                <meshStandardMaterial color="#67e8f9" emissive="#22d3ee" emissiveIntensity={0.8} transparent opacity={0.9} />
            </mesh>
            <mesh position={[-0.9, 0.11, 0.3]} rotation={[0, -0.8, 0]}>
                <octahedronGeometry args={[0.09, 0]} />
                <meshStandardMaterial color="#a5f3fc" emissive="#0891b2" emissiveIntensity={0.8} transparent opacity={0.9} />
            </mesh>
        </group>
    )
}

// BlockSpinner - Rotating obstacle with varied shape
export function BlockSpinner({ position = [0, 0, 0] })
{
    const obstacle = useRef()
    const [speed] = useState(() => (Math.random() + 0.3) * (Math.random() < 0.5 ? -1 : 1))
    const [variant] = useState(() => Math.floor(Math.random() * 3))

    useFrame((state) => {
        if (!obstacle.current) return
        const time = state.clock.getElapsedTime()
        const rotation = new THREE.Quaternion()
        rotation.setFromEuler(new THREE.Euler(0, time * speed, 0))
        obstacle.current.setNextKinematicRotation(rotation)
    })

    // Different obstacle shapes
    const obstacleScales = [
        [3.5, 0.3, 0.3],   // Thin bar
        [3.2, 0.5, 0.4],   // Thick bar
        [3.8, 0.25, 0.5],  // Wide thin bar
    ]
    const scale = obstacleScales[variant]

    return (
        <group position={position}>
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
            </RigidBody>

            <RigidBody ref={obstacle} type="kinematicPosition" position={[0, 0.3, 0]} restitution={0.2} friction={0}>
                <mesh
                    geometry={boxGeometry}
                    scale={scale}
                    castShadow
                    receiveShadow
                    material={obstacleMaterial}
                />
            </RigidBody>
        </group>
    )
}

// BlockLimbo - Vertical moving obstacle with varied shape
export function BlockLimbo({ position = [0, 0, 0] })
{
    const obstacle = useRef()
    const [timeOffset] = useState(() => Math.random() * Math.PI * 2)
    const [variant] = useState(() => Math.floor(Math.random() * 3))

    useFrame((state) => {
        if (!obstacle.current) return
        const time = state.clock.getElapsedTime()
        const y = Math.sin(time + timeOffset) + 1.15
        obstacle.current.setNextKinematicTranslation({ x: position[0], y: position[1] + y, z: position[2] })
    })

    const obstacleScales = [
        [3.5, 0.3, 0.3],   // Standard
        [3.0, 0.4, 0.5],   // Thick
        [3.8, 0.2, 0.35],  // Long thin
    ]
    const scale = obstacleScales[variant]

    return (
        <group position={position}>
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
            </RigidBody>

            <RigidBody ref={obstacle} type="kinematicPosition" position={[0, 0.3, 0]} restitution={0.2} friction={0}>
                <mesh
                    geometry={boxGeometry}
                    scale={scale}
                    castShadow
                    receiveShadow
                    material={obstacleMaterial}
                />
            </RigidBody>
        </group>
    )
}

// BlockAxe - Horizontal moving obstacle with varied shape
export function BlockAxe({ position = [0, 0, 0] })
{
    const obstacle = useRef()
    const [timeOffset] = useState(() => Math.random() * Math.PI * 2)
    const [variant] = useState(() => Math.floor(Math.random() * 3))

    useFrame((state) => {
        if (!obstacle.current) return
        const time = state.clock.getElapsedTime()
        const x = Math.sin(time + timeOffset) * 1.25
        obstacle.current.setNextKinematicTranslation({ x: position[0] + x, y: position[1] + 0.75, z: position[2] })
    })

    const obstacleScales = [
        [1.5, 1.5, 0.3],   // Square
        [1.2, 1.8, 0.4],   // Tall
        [1.8, 1.2, 0.35],  // Wide
    ]
    const scale = obstacleScales[variant]

    return (
        <group position={position}>
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
            </RigidBody>

            <RigidBody ref={obstacle} type="kinematicPosition" position={[0, 0.3, 0]} restitution={0.2} friction={0}>
                <mesh
                    geometry={boxGeometry}
                    scale={scale}
                    castShadow
                    receiveShadow
                    material={obstacleMaterial}
                />
            </RigidBody>
        </group>
    )
}

// BlockIceWater - Danger zone
export function BlockIceWater({ position = [0, 0, 0] })
{
    const fail = useGame((state) => state.fail)
    const waterRef = useRef()

    useFrame((state) => {
        const time = state.clock.getElapsedTime()
        if (waterRef.current) {
            waterRef.current.material.emissiveIntensity = 0.3 + Math.sin(time * 2) * 0.15
        }
    })

    const handleEnterWater = () => {
        const currentPhase = useGame.getState().phase
        if (currentPhase === 'playing') {
            fail()
        }
    }

    return (
        <group position={position}>
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[0.5, 0.1, 2]} position={[-1.5, -0.1, 0]} />
                <CuboidCollider args={[0.5, 0.1, 2]} position={[1.5, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[1, 0.2, 4]}
                    position={[-1.5, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
                <mesh
                    geometry={boxGeometry}
                    scale={[1, 0.2, 4]}
                    position={[1.5, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
            </RigidBody>

            <RigidBody
                type="fixed"
                colliders={false}
                sensor
                onIntersectionEnter={handleEnterWater}
            >
                <CuboidCollider args={[0.6, 0.15, 1.5]} position={[0, -0.4, 0]} sensor />
            </RigidBody>

            <mesh ref={waterRef} position={[0, -0.15, 0]}>
                <boxGeometry args={[2, 0.5, 4]} />
                <meshStandardMaterial
                    color="#0c4a6e"
                    emissive="#0369a1"
                    emissiveIntensity={0.3}
                    metalness={0.9}
                    roughness={0.1}
                    transparent
                    opacity={0.8}
                />
            </mesh>

            <mesh position={[0, -0.45, 0]}>
                <boxGeometry args={[2, 0.3, 4]} />
                <meshStandardMaterial color="#082f49" transparent opacity={0.95} />
            </mesh>
        </group>
    )
}

// BlockDoubleSpinner - Hard level with varied shapes
export function BlockDoubleSpinner({ position = [0, 0, 0] })
{
    const obstacle1 = useRef()
    const obstacle2 = useRef()
    const [speed] = useState(() => (Math.random() + 0.4))

    useFrame((state) => {
        if (!obstacle1.current || !obstacle2.current) return
        const time = state.clock.getElapsedTime()

        const rotation1 = new THREE.Quaternion()
        rotation1.setFromEuler(new THREE.Euler(0, time * speed, 0))
        obstacle1.current.setNextKinematicRotation(rotation1)

        const rotation2 = new THREE.Quaternion()
        rotation2.setFromEuler(new THREE.Euler(0, -time * speed * 1.5, 0))
        obstacle2.current.setNextKinematicRotation(rotation2)
    })

    return (
        <group position={position}>
            <RigidBody type="fixed" restitution={0.2} friction={1} colliders={false}>
                <CuboidCollider args={[2, 0.1, 2]} position={[0, -0.1, 0]} />
                <mesh
                    geometry={boxGeometry}
                    scale={[4, 0.2, 4]}
                    position={[0, -0.1, 0]}
                    receiveShadow
                    material={floor2Material}
                />
            </RigidBody>

            <RigidBody ref={obstacle1} type="kinematicPosition" position={[0, 0.3, 0]} restitution={0.2} friction={0}>
                <mesh
                    geometry={boxGeometry}
                    scale={[3.5, 0.35, 0.35]}
                    castShadow
                    receiveShadow
                    material={obstacleMaterial}
                />
            </RigidBody>

            <RigidBody ref={obstacle2} type="kinematicPosition" position={[0, 0.85, 0]} restitution={0.2} friction={0}>
                <mesh
                    geometry={boxGeometry}
                    scale={[2.8, 0.3, 0.4]}
                    castShadow
                    receiveShadow
                    material={obstacleMaterial}
                />
            </RigidBody>
        </group>
    )
}

// Start back wall material - very transparent
const startBackWallMaterial = new THREE.MeshStandardMaterial({
    color: '#a5f3fc',
    metalness: 0.8,
    roughness: 0.1,
    transparent: true,
    opacity: 0.2
})

// Continuous walls component - taller walls
function ContinuousWalls({ count })
{
    const totalLength = (count + 2) * 4
    const centerZ = -totalLength / 2 + 2
    // Back wall should be at the back edge of finish platform
    // Finish is at -(count + 1) * 4, platform is 4 units, so back edge is -(count + 1) * 4 - 2
    const backWallZ = -(count + 1) * 4 - 2
    // Front wall at start - behind the start platform
    const frontWallZ = 2

    // Wall height - taller to prevent escaping
    const wallHeight = 3
    const wallY = wallHeight / 2

    return (
        <>
            {/* Right wall - floor bilan birlashtirilgan (2.0 pozitsiyada) */}
            <RigidBody type="fixed" restitution={0.2} friction={0} colliders={false}>
                <CuboidCollider args={[0.15, wallHeight / 2, totalLength / 2]} position={[2.0, wallY, centerZ]} />
                <mesh
                    position={[2.0, wallY, centerZ]}
                    geometry={boxGeometry}
                    material={wallMaterial}
                    scale={[0.3, wallHeight, totalLength]}
                    castShadow
                />
            </RigidBody>

            {/* Left wall - floor bilan birlashtirilgan (2.0 pozitsiyada) */}
            <RigidBody type="fixed" restitution={0.2} friction={0} colliders={false}>
                <CuboidCollider args={[0.15, wallHeight / 2, totalLength / 2]} position={[-2.0, wallY, centerZ]} />
                <mesh
                    position={[-2.0, wallY, centerZ]}
                    geometry={boxGeometry}
                    material={wallMaterial}
                    scale={[0.3, wallHeight, totalLength]}
                    receiveShadow
                />
            </RigidBody>

            {/* Back wall at finish */}
            <RigidBody type="fixed" restitution={0.2} friction={0} colliders={false}>
                <CuboidCollider args={[2.15, wallHeight / 2, 0.15]} position={[0, wallY, backWallZ]} />
                <mesh
                    position={[0, wallY, backWallZ]}
                    geometry={boxGeometry}
                    material={wallMaterial}
                    scale={[4.3, wallHeight, 0.3]}
                    receiveShadow
                />
            </RigidBody>

            {/* Front wall at start - transparent */}
            <RigidBody type="fixed" restitution={0.2} friction={0} colliders={false}>
                <CuboidCollider args={[2.15, wallHeight / 2, 0.15]} position={[0, wallY, frontWallZ]} />
                <mesh
                    position={[0, wallY, frontWallZ]}
                    geometry={boxGeometry}
                    material={startBackWallMaterial}
                    scale={[4.3, wallHeight, 0.3]}
                />
            </RigidBody>
        </>
    )
}

// Winter decorations outside the track - reduced by 40%
function WinterDecorations({ count })
{
    const decorations = useMemo(() => {
        const items = []
        const totalLength = (count + 2) * 4

        // Trees on both sides - reduced frequency
        for (let i = 0; i < count + 3; i++) {
            const z = -i * 4 - 2

            // === RIGHT SIDE ===
            // Close trees
            if (Math.random() > 0.5) {
                items.push({
                    type: 'tree',
                    position: [3.5 + Math.random() * 1.5, 0, z + Math.random() * 3 - 1.5],
                    scale: 0.7 + Math.random() * 0.5
                })
            }
            // Medium distance trees
            if (Math.random() > 0.55) {
                items.push({
                    type: 'tree',
                    position: [5.5 + Math.random() * 2, 0, z + Math.random() * 3 - 1],
                    scale: 0.9 + Math.random() * 0.7
                })
            }
            // Far trees
            if (Math.random() > 0.6) {
                items.push({
                    type: 'tree',
                    position: [8 + Math.random() * 3, 0, z + Math.random() * 4 - 2],
                    scale: 1.0 + Math.random() * 0.8
                })
            }

            // === LEFT SIDE ===
            // Close trees
            if (Math.random() > 0.5) {
                items.push({
                    type: 'tree',
                    position: [-3.5 - Math.random() * 1.5, 0, z + Math.random() * 3 - 1.5],
                    scale: 0.7 + Math.random() * 0.5
                })
            }
            // Medium distance trees
            if (Math.random() > 0.55) {
                items.push({
                    type: 'tree',
                    position: [-5.5 - Math.random() * 2, 0, z + Math.random() * 3 - 1],
                    scale: 0.9 + Math.random() * 0.7
                })
            }
            // Far trees
            if (Math.random() > 0.6) {
                items.push({
                    type: 'tree',
                    position: [-8 - Math.random() * 3, 0, z + Math.random() * 4 - 2],
                    scale: 1.0 + Math.random() * 0.8
                })
            }

            // === SNOWMEN ===
            if (Math.random() > 0.85) {
                const side = Math.random() > 0.5 ? 1 : -1
                items.push({
                    type: 'snowman',
                    position: [side * (4 + Math.random() * 3), 0, z + Math.random() * 2],
                    scale: 0.7 + Math.random() * 0.5
                })
            }

            // === ICE ROCKS ===
            if (Math.random() > 0.7) {
                items.push({
                    type: 'rock',
                    position: [
                        (Math.random() > 0.5 ? 1 : -1) * (3.5 + Math.random() * 4),
                        0.1,
                        z + Math.random() * 3
                    ],
                    scale: 0.4 + Math.random() * 0.6
                })
            }
        }

        return items
    }, [count])

    return (
        <>
            {decorations.map((item, index) => {
                if (item.type === 'tree') {
                    return <PineTree key={index} position={item.position} scale={item.scale} />
                } else if (item.type === 'snowman') {
                    return <Snowman key={index} position={item.position} />
                } else if (item.type === 'rock') {
                    return <IceRock key={index} position={item.position} scale={item.scale} />
                }
                return null
            })}
        </>
    )
}

// Level - Main level component
export function Level({ count = 12, seed = 0 })
{
    const currentLevel = useGame((state) => state.currentLevel)
    const levelSettings = useGame((state) => state.levelSettings)

    const { blocks, crystalPositions } = useMemo(() => {
        const blocks = []
        const crystalPositions = []
        const dangerPositions = []
        const settings = levelSettings[currentLevel]

        const baseTypes = [BlockSpinner, BlockLimbo, BlockAxe]

        if (currentLevel === 3) {
            baseTypes.push(BlockDoubleSpinner)
        }

        const crystalCount = settings.crystals
        const step = Math.floor(count / (crystalCount + 1))
        for (let i = 1; i <= crystalCount; i++) {
            const pos = Math.min(i * step, count - 1)
            crystalPositions.push(pos)
        }

        const dangerCount = settings.dangers
        if (dangerCount > 0) {
            const availablePositions = []
            for (let i = 2; i < count - 1; i++) {
                if (!crystalPositions.includes(i)) {
                    availablePositions.push(i)
                }
            }
            for (let i = availablePositions.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [availablePositions[i], availablePositions[j]] = [availablePositions[j], availablePositions[i]]
            }
            for (let i = 0; i < Math.min(dangerCount, availablePositions.length); i++) {
                dangerPositions.push(availablePositions[i])
            }
        }

        for (let i = 0; i < count; i++) {
            if (dangerPositions.includes(i)) {
                blocks.push({ type: BlockIceWater, hasCrystal: false })
            } else {
                const type = baseTypes[Math.floor(Math.random() * baseTypes.length)]
                blocks.push({ type, hasCrystal: crystalPositions.includes(i) })
            }
        }

        return { blocks, crystalPositions }
    }, [count, seed, currentLevel, levelSettings])

    return (
        <>
            {/* Winter decorations */}
            <WinterDecorations count={count} />

            {/* Continuous side walls */}
            <ContinuousWalls count={count} />

            <BlockStart position={[0, 0, 0]} />

            {blocks.map((block, index) => (
                <group key={index}>
                    <block.type position={[0, 0, -(index + 1) * 4]} />
                    {block.hasCrystal && (
                        <Crystal position={[0, 0.3, -(index + 1) * 4]} />
                    )}
                </group>
            ))}

            <BlockEnd position={[0, 0, -(count + 1) * 4]} />
        </>
    )
}
